<template>
  <v-app>
    <AppSidebar v-if="isAuthenticated" />
    <AppHeader v-if="isAuthenticated" />
    
    <v-main 
      :class="{ 'main-full': !isAuthenticated }" 
      :style="isAuthenticated ? { paddingLeft: sidebarWidth + 'px' } : {}"
    >
      <v-container v-if="isAuthenticated" fluid>
        <router-view :key="$route.fullPath" />
      </v-container>
      <template v-else>
        <router-view :key="$route.fullPath" />
      </template>
    </v-main>
    
    <AppFooter v-if="isAuthenticated" />
    
    <!-- Global notifications -->
    <v-snackbar
      v-model="notification.show"
      :color="notification.color"
      :timeout="notification.timeout"
    >
      {{ notification.message }}
    </v-snackbar>
  </v-app>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, onUnmounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useNotificationStore } from '@/stores/notification'
import AppSidebar from '@/components/common/AppSidebar.vue'
import AppHeader from '@/components/common/AppHeader.vue'
import AppFooter from '@/components/common/AppFooter.vue'

const authStore = useAuthStore()
const notificationStore = useNotificationStore()

const isAuthenticated = computed(() => authStore.isAuthenticated)
const notification = computed(() => notificationStore.current)
const sidebarWidth = ref(260)

// Handle sidebar toggle event
function handleSidebarToggle(event: CustomEvent) {
  sidebarWidth.value = event.detail.isMini ? 80 : 260
}

// Fetch user data on app mount if token exists
onMounted(() => {
  console.log('App.vue mounted, checking auth:', { 
    hasToken: !!authStore.token, 
    hasUser: !!authStore.user,
    isAuth: authStore.isAuthenticated 
  })
  
  // Initialize default date format for new users
  if (!localStorage.getItem('appSettings')) {
    const defaultSettings = {
      language: 'English',
      dateFormat: 'DD/MM/YYYY',
      currency: 'IDR - Indonesian Rupiah',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    }
    localStorage.setItem('appSettings', JSON.stringify(defaultSettings))
  }
  
  if (authStore.token && !authStore.user) {
    console.log('Fetching user data...')
    authStore.fetchUser()
  }
  
  // Listen for sidebar toggle events
  window.addEventListener('sidebar-toggle', handleSidebarToggle as EventListener)
  
  // Initialize sidebar width from localStorage
  const savedState = localStorage.getItem('sidebarMini')
  if (savedState === 'true') {
    sidebarWidth.value = 80
  }
})

onUnmounted(() => {
  window.removeEventListener('sidebar-toggle', handleSidebarToggle as EventListener)
})
</script>

<style scoped>
:deep(v-main) {
  transition: padding-left 0.3s ease;
  background-color: #F5F7FA;
}

.main-full {
  padding-left: 0 !important;
  background-color: #F5F7FA;
}
</style>